import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UpdateCustomerBodyDto, UpdateSubscriptionBody } from '@nusign/picflow-backend-types';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';

import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import type { Stripe } from 'stripe';
import { freeStripeProductPrice } from '../billing-consts';
import { findSubscriptionLineItem, isFreeSubscription } from '../lib/subscription-helpers';
import { TENANT_CONTEXT } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { Promotion } from '../types/promotion.type';
import { CardService } from './card.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { PricingService } from './pricing.service';
import { PromotionService } from './promotion.service';
import { SentryService } from './sentry.service';
import { StateService } from './state.service';
import { StripeService } from './stripe.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private pricingService = inject(PricingService);
  private state = inject(StateService);
  private cardService = inject(CardService);
  private promotionService = inject(PromotionService);
  private toasts = inject(ToastService);
  private stripeService = inject(StripeService);
  private sentryService = inject(SentryService);
  private observableLoader = inject(ObservableDataLoaderService);
  private permissionService = inject(PermissionService);

  private handlePayment(payment_intent: PaymentIntent): Observable<boolean | StripeError> {
    if (payment_intent.status !== 'requires_action') return of(true);
    return this.confirmCardPayment(payment_intent.client_secret).pipe(
      map(({ error }) => {
        if (!error) return true;
        console.error(error);
        this.toasts.add('Payment confirmation failed', 'error');
        return error;
      })
    );
  }

  private confirmCardPayment(secret: string) {
    return this.cardService.getCardData().pipe(
      filter(Boolean),
      take(1),
      switchMap(cardData => {
        return from(
          this.stripeService.confirmCardPayment(secret, {
            payment_method: cardData.paymentMethod,
          })
        );
      })
    );
  }

  public updateSubscription(params: UpdateSubscriptionBody) {
    return this.http.patch<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, params).pipe(
      switchMap(resp => {
        return from(this.refreshPermissions()).pipe(
          switchMap(() => {
            this.observableLoader.forceDataRefreshFuzzy(`subscription_`);
            if (!(resp.latest_invoice as Stripe.Invoice).payment_intent) return of(true);
            return this.handlePayment(
              (resp.latest_invoice as Stripe.Invoice).payment_intent as unknown as PaymentIntent
            );
          }),
          catchError(err => {
            return of(this.createOrupdateSubscriptionError(err));
          })
        );
      })
    );
  }

  public getSubscription() {
    return this.state.activeTenant$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id && a?.id_stripe_subscription === b?.id_stripe_subscription),
      switchMap(tenant => {
        if (!tenant) return of(undefined);
        if (!tenant.id_stripe_subscription) {
          return of(<Stripe.Subscription>{
            status: 'active',
          });
        }
        return this.observableLoader.get(`subscription_${tenant.id}`, () => {
          return this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, {
            context: new HttpContext().set(TENANT_CONTEXT, tenant.id),
          });
        });
      })
    );
  }

  public getMainPriceSubLine() {
    return this.getSubscription().pipe(
      take(1),
      map(stripeSub => {
        if (!stripeSub || isFreeSubscription(stripeSub)) return undefined;
        return findSubscriptionLineItem(stripeSub.items.data, 'main');
      })
    );
  }

  private notifiedAboutMainPrice = false;
  public getMainPrice() {
    return this.state.activeTenant$.pipe(
      take(1),
      switchMap(tenant => {
        if (!tenant) return of(null);

        return forkJoin([
          this.pricingService.getPrices('main').pipe(take(1)),
          this.getSubscription().pipe(take(1)),
        ]).pipe(
          map(([mainPrices, stripeSub]) => {
            if (!stripeSub || isFreeSubscription(stripeSub)) return freeStripeProductPrice;
            const planItem = findSubscriptionLineItem(stripeSub.items.data, 'main');
            const planPriceFromMainPrices = mainPrices.find(p => p.id === planItem.price.id);
            if (!planPriceFromMainPrices) {
              if (!this.notifiedAboutMainPrice) {
                console.warn('Could not find price in main prices, using subscription price which may be outdated.');
                this.notifiedAboutMainPrice = true;
              }
            }
            return (planPriceFromMainPrices || planItem.price) as Stripe.Price & { product: Stripe.Product };
          })
        );
      })
    );
  }

  private getPromoFromDiscount(discount: Stripe.Discount) {
    const promoCode = discount?.promotion_code as Stripe.PromotionCode;
    const currentPromoCode = promoCode?.code;
    if (currentPromoCode) {
      return from(this.promotionService.checkCode(promoCode?.code));
    }

    if (discount) {
      // This is needed for when a promotion doesn't have a code
      return of(this.promotionService.convertPromo(discount as unknown as Stripe.PromotionCode));
    }
  }

  public getCurrentDiscount() {
    return this.getSubscription().pipe(
      map(sub => sub?.discount),
      switchMap(discount => {
        if (!discount) return of(undefined);
        return this.getPromoFromDiscount(discount).pipe(
          map(promo => {
            if (!promo) return undefined;
            return {
              promo,
              start: discount.start * 1000,
              end: discount.end * 1000,
            };
          })
        );
      })
    );
  }

  public createSubscription(price: Stripe.Price, promo?: Promotion, startTrial = false) {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];
    const trialLength = this.pricingService.getTrialLengthDays();
    const params = {
      prices,
      trial_days: startTrial ? trialLength : 0,
      promotion_code: promo?.code,
    };

    return this.http.post<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, params).pipe(
      switchMap(resp => {
        if (!(resp.latest_invoice as Stripe.Invoice).payment_intent) return of(true);
        return this.handlePayment((resp.latest_invoice as Stripe.Invoice).payment_intent as unknown as PaymentIntent);
      }),
      switchMap(() => this.refreshPermissions()),
      catchError(err => {
        return of(this.createOrupdateSubscriptionError(err));
      })
    );
  }

  private refreshPermissions() {
    return this.state.activeTenant$.pipe(
      take(1),
      // delay to allow the backend to update the subscription from the stripe webhook
      delay(2500),
      switchMap(tenant => {
        this.observableLoader.remove(`tenant_permissions_${tenant.id}`);
        return this.permissionService.getTenantBillingPermissions(tenant.id);
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createOrupdateSubscriptionError(err: any) {
    const message = ((): string => {
      if (!err?.error?.message)
        return 'There was an error charging your card. Please update your payment method or contact support';
      const error: string = err.error.message;
      if (error.includes('requires additional user action'))
        return 'There was an error charging your card. Please update your payment method or contact support';
      return err.error?.message;
    })();
    // const message = err?.error?.message || 'There was an error charging your card.';
    this.toasts.add(message, 'error', { duration: 5 * 1000 });
    console.error(err);
    this.sentryService.captureException('change-plan-failed');
    return false;
  }

  public changeAddonsCount(items: { priceId: string; quantity: number }[]) {
    const prices = items.map(item => ({
      id: item.priceId,
      quantity: item.quantity,
      deleted: item.quantity === 0 ? true : undefined,
    }));
    return this.updateSubscription({ prices });
  }

  public cancel() {
    return this.http.delete<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`).pipe(
      tap(() => {
        this.observableLoader.forceDataRefreshFuzzy(`subscription_`);
      })
    );
  }

  public reActivate() {
    return this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`).pipe(
      switchMap(sub => {
        const prices = sub.items.data.map(item => ({
          id: item.price.id,
          quantity: item.quantity,
        }));
        return this.updateSubscription({ prices });
      })
    );
  }

  public extendTrial(
    days: number,
    subscription: Stripe.Subscription,
    mainPrice: Stripe.Price & { product: Stripe.Product }
  ) {
    const timeLeftOnTrialMs = subscription.trial_end * 1000 - Date.now();
    const timeLeftOnTrialDays = Math.ceil(timeLeftOnTrialMs / (1000 * 60 * 60 * 24));
    const params: UpdateSubscriptionBody = {
      trial_days: timeLeftOnTrialDays + days,
      prices: [
        {
          id: mainPrice.id,
        },
      ],
    };
    return this.updateSubscription(params);
  }

  public updateBillingInfo(params: UpdateCustomerBodyDto) {
    return this.http.patch(`${this.config.apiEndpoint}/billing/customer`, params);
  }
}
